/* eslint-disable @typescript-eslint/naming-convention */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Field, FormSpy, useFormState } from 'react-final-form';
import { connect, useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import { getIn } from 'final-form';

import { DECISION_ACT } from 'CorePackage/MainModule/routes/routes';
import { Button } from 'UIPackage/MainModule/components/Button';
import { Section } from 'UIPackage/FormModule/components/Section';
import { KnmCollapse } from 'KnmPackage/MainModule/components/KnmCollapse';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { IDecisionForm } from 'DecisionPackage/MainModule/interfaces/IDecisionForm';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { getUserRoleIsSupervisor } from 'AuthPackage/MainModule/selectors/authSelectors';
import {
  getDecisionDataDecisionId,
  getDecisionDataResigningRequired,
  getKnmIsDecisionStatusFillInProcess,
  getKnmIsDecisionStatusTypeCompleted,
} from 'DecisionPackage/MainModule/selectors/decisionSelectors';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { getIsDirtyAct } from 'KnmErknmPackage/ErknmActsModule/components/ErknmPaperlessActComponents/components/ErknmActSignButton/getIsDirtyAct';
import { getHasModifiedDirtyFields } from 'KnmPackage/MainModule/common/getHasModifiedDirtyFields';
import { IDecisionActForm } from 'DecisionPackage/MainModule/interfaces/IDecisionActForm';
import { knmSetIsNeedActSaveOrSign } from 'KnmPackage/MainModule/actions/knmActions';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { HadToBeHowInSignMessage } from 'KnmErknmPackage/MainModule/components/HadToBeHowInSignMessage';
import { IDecisionFormReason } from 'DecisionPackage/MainModule/interfaces/IDecisionFormReason';

import { DecisionActItem } from './DecisionActItem';
import { DecisionPaperlessActItem } from './DecisionPaperlessActItem/DecisionPaperlessActItem';
import { DecisionActStatus } from './DecisionActStatus';
import { checkHasErrorInCollapseBlock } from '../../functions/checkHasErrorInCollapseBlock';
import { getTitleFieldWithRedStar } from '../../functions/getTitleFieldWithRedStar';
import styles from './DecisionAct.module.scss';
import { isLaterThanAdminSetting } from '../../CorePackage/MainModule/validators/isLaterThanAdminSetting';

interface IDecisionActProps {
  title: string;
  formRef: IDecisionFormRef | undefined;
  isWatchOnly: boolean;
  values: IDecisionForm;
  reasons: IDecisionFormReason[] | undefined;
  isEnablePaperlessDocument?: boolean;
}

interface IDecisionActStoreProps {
  isSupervisor: boolean;
  isDecisionStatusFillInProcess: boolean;
  isDecisionStatusTypeComplete: boolean;
  resigningRequired: boolean;
  settings: IAdminSetting[];
  decisionId: string | undefined;
}

function DecisionActWithoutStore({
  formRef,
  title,
  isSupervisor,
  isDecisionStatusFillInProcess,
  isDecisionStatusTypeComplete,
  isWatchOnly,
  resigningRequired,
  settings,
  values,
  decisionId,
  reasons,
  isEnablePaperlessDocument,
}: IDecisionActProps & IDecisionActStoreProps) {
  const [isKnmCollapseOpened, setIsKnmCollapseOpened] = useState<boolean>(true);
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const isRequirements = values?.isViolatedMandatoryRequirements;
  const supervisionType = values?.kindControl;
  const inspectors = values?.inspectors;
  const knoOrganization = values?.knoOrganization;
  const isNeedToAddAct = values?.isNeedToAddAct;
  const violatedMandatoryRequirements = values?.violatedMandatoryRequirements;
  const organization = values?.organization;
  const objects = values?.objects;
  const act = organization?.act;

  const isDisableAct = !isNeedToAddAct?.value || !isSupervisor || !isDecisionStatusFillInProcess || isWatchOnly;
  const isDisableViolationFields = !isNeedToAddAct?.value || !isSupervisor || isWatchOnly;
  const name = 'organization.act';

  const isEnablePaperless = settings?.find(
    setting => setting.code === AdminSettingCodesEnum.ENABLED_PAPERLESS_PASSPORT_ACT_PRESCRIPTION,
  );

  const isEnablePaperlessPassportDocument = isLaterThanAdminSetting(
    values.created,
    settings,
    AdminSettingCodesEnum.ENABLED_PAPERLESS_PASSPORT_4_1_3_STARTS_FROM,
  );

  /**
   * Обработчик кнопки "Добавить"
   */
  const addAct = useCallback(() => {
    if (!isDisableAct) {
      // @ts-ignore
      formRef?.current.change(name, {
        guid: uuidv4(),
        isNew: true,
        isViolatedMandatoryRequirements: isEnablePaperless?.value === 'false' ? undefined : isRequirements,
        violatedMandatoryRequirements: isEnablePaperless?.value === 'false' ? undefined : violatedMandatoryRequirements,
        paperless: isEnablePaperless?.value === 'true',
        numberAct: isEnablePaperless?.value === 'true' ? `${decisionId}-1` : undefined,
      });
    }
  }, [isDisableAct, formRef, isRequirements, isEnablePaperless, decisionId]);

  /**
   * Обработчик удаления акта
   */
  const onRemoveAct = useCallback(() => {
    if (!isDisableAct) {
      formRef?.current.change(name as keyof IDecisionForm);
    }
  }, [isDisableAct, formRef]);

  const { dirtyFields: currentDirtyFields, dirty: isDirty, values: currentValues } = useFormState({
    subscription: { dirtyFields: true, dirty: true, values: true },
  });

  /**
   * Расчет блокировки кнопки "Добавить" при наличии изменений в паспорте (за исключением акта)
   */
  const isDisabledByDirty = useMemo(() => {
    if (isDecisionStatusTypeComplete) {
      const { wasEditFiled: isDirtyBySigned } = getIsDirtyAct(currentDirtyFields, 0, false, true, formRef);

      // Исключение полей акта для проверки на modified
      const formDirtyFieldsWithoutActs = Object.entries(currentDirtyFields).reduce((acc, value) => {
        return !value[0].includes('organization.act') && value[0] !== 'organization'
          ? { ...acc, [value[0]]: value[1] }
          : acc;
      }, {});

      const hasModifiedDirtyFields = getHasModifiedDirtyFields(formDirtyFieldsWithoutActs, formRef);

      return !isDirtyBySigned && isDirty && hasModifiedDirtyFields;
    }

    return false;
  }, [formRef, isDecisionStatusTypeComplete, currentDirtyFields, isDirty, currentValues]);

  /**
   * Проверка наличия необходимости сохранения или подписания одного из актов
   */
  useEffect(() => {
    if (isDecisionStatusTypeComplete && isEnablePaperlessPassportDocument && isEnablePaperless?.value === 'true') {
      if (act) {
        const getIsNeedActSaveOrSign = (currentAct: IDecisionActForm) => {
          if (!currentAct.isNew) {
            // Алгоритм аналогичный ErknmActSignButton
            if (currentAct.signed) {
              const { wasEditFiled: isDirtyBySigned } = getIsDirtyAct(currentDirtyFields, 0, false, true, formRef);

              return isDirtyBySigned;
            }

            return true;
          }

          return true;
        };

        const isNeedActSaveOrSign = getIsNeedActSaveOrSign(act);

        dispatch(knmSetIsNeedActSaveOrSign(isNeedActSaveOrSign));
      } else {
        dispatch(knmSetIsNeedActSaveOrSign(false));
      }
    }

    return () => {
      dispatch(knmSetIsNeedActSaveOrSign(false));
    };
  }, [act, isDirty, isDecisionStatusTypeComplete, currentDirtyFields]);

  const helpHit = useMemo(() => {
    if (!isDecisionStatusTypeComplete) {
      return undefined;
    }

    if (act && !act.isNew && !act.signed && isEnablePaperlessDocument) {
      return <HadToBeHowInSignMessage message={'Необходимо переподписать изменения в акте'} />;
    }
    if (isDisabledByDirty || resigningRequired) {
      return <HadToBeHowInSignMessage message={'Необходимо сохранить и подписать изменения в решении'} />;
    }

    return undefined;
  }, [isDisabledByDirty, resigningRequired, isDecisionStatusTypeComplete, act]);

  const hasError = checkHasErrorInCollapseBlock(formRef, name);

  return (
    <Field name={name}>
      {({ input }) => {
        return (
          <Section
            {...input}
            title={getTitleFieldWithRedStar({
              label: title,
              field: name,
              sectionName: name,
              isDecision: true,
            })}
            id={DECISION_ACT}
            type="list"
            customButton={
              <Button
                className="shared-btn-add"
                text="Добавить"
                type="button"
                variant="link"
                color="primary"
                disabled={isDisableAct || !!act || resigningRequired || isDisabledByDirty}
                onClick={addAct}
              />
            }
          >
            <FormSpy subscription={{ errors: true, touched: true }}>
              {props => {
                const fieldError = getIn(props.errors || {}, name);
                const isTouched = props.touched?.[name];

                if (isTouched && fieldError && typeof fieldError === 'string') {
                  return (
                    <p className={`shared-input-error shared-row__small-left-margin ${styles.ErrorMsgWrapper}`}>
                      {fieldError}
                    </p>
                  );
                }

                return null;
              }}
            </FormSpy>
            <div>{helpHit}</div>
            {act && (
              <KnmCollapse
                theme="fields"
                title={'Акт'}
                subtitle={act.numberAct}
                isNew={!act.paperless && act.isNew}
                opened={!act?.fileInvalidate}
                onClose={act.isNew ? onRemoveAct : undefined}
                headerPostfix={<DecisionActStatus act={act} />}
                changeOpenedCallback={setIsKnmCollapseOpened}
                error={isKnmCollapseOpened && hasError}
              >
                {act.paperless ? (
                  <DecisionPaperlessActItem
                    value={act}
                    watchOnly={isDisableAct || !!act.fileInvalidate}
                    isDisabledByDirty={isDisabledByDirty}
                    isViolationFieldsWatchOnly={isDisableViolationFields || !!act.fileInvalidate}
                    name={name}
                    inspectors={inspectors}
                    knoOrganization={knoOrganization}
                    supervisionType={supervisionType}
                    objects={objects}
                    organization={organization}
                    created={values.created}
                    basisDocumentDateTime={values.basisDocumentDateTime}
                  />
                ) : (
                  <DecisionActItem
                    isRequirements={isRequirements}
                    knoOrganization={knoOrganization}
                    supervisionType={supervisionType}
                    objects={objects}
                    watchOnly={isDisableAct}
                    isViolationFieldsWatchOnly={isDisableViolationFields}
                    act={act}
                    formRef={formRef}
                    name={name}
                    created={values.created}
                    reasons={reasons}
                  />
                )}
              </KnmCollapse>
            )}
          </Section>
        );
      }}
    </Field>
  );
}

const mapStateToProps = (state: IRootState): IDecisionActStoreProps => ({
  isSupervisor: getUserRoleIsSupervisor(state),
  isDecisionStatusFillInProcess: getKnmIsDecisionStatusFillInProcess(state),
  isDecisionStatusTypeComplete: getKnmIsDecisionStatusTypeCompleted(state),
  resigningRequired: getDecisionDataResigningRequired(state),
  settings: getAdminFetchSettings(state),
  decisionId: getDecisionDataDecisionId(state),
});

export const DecisionAct = connect(mapStateToProps)(DecisionActWithoutStore);
